import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LOGO } from "../lib/site";
import { LogOut, Mail, Users } from "lucide-react";

const Tab = ({ active, onClick, children, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`px-6 py-3 text-sm tracking-[0.15em] uppercase transition-colors border-b-2 ${
      active ? "border-cobalt text-cobalt" : "border-transparent text-ink-soft hover:text-ink"
    }`}
  >
    {children}
  </button>
);

const AdminDashboard = () => {
  const { user, logout, api } = useAuth();
  const [tab, setTab] = useState("contacts");
  const [contacts, setContacts] = useState([]);
  const [newsletter, setNewsletter] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [c, n] = await Promise.all([api.get("/admin/contacts"), api.get("/admin/newsletter")]);
      setContacts(c.data);
      setNewsletter(n.data);
    } catch {
      /* handled by auth redirect */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard-page">
      <header className="border-b border-tan bg-cream/95 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO} alt="Elenem" className="h-10 w-10 object-contain" />
            <span className="font-display text-lg text-ink">
              Elenem <em className="text-cobalt">Admin</em>
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-sm text-ink-soft hidden sm:inline" data-testid="admin-current-user">
              {user?.email}
            </span>
            <button
              onClick={handleLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 border border-ink text-ink px-4 py-2 text-xs tracking-[0.15em] uppercase hover:bg-ink hover:text-cream transition-colors"
            >
              <LogOut size={14} /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        <h1 className="font-display text-4xl lg:text-5xl text-ink">Dashboard</h1>
        <p className="text-ink-soft mt-2">Submissions from the launch site.</p>

        <div className="mt-10 grid sm:grid-cols-2 gap-4 max-w-2xl">
          <div className="bg-cream-alt p-6" data-testid="stat-contacts">
            <div className="flex items-center gap-3 text-cobalt">
              <Mail size={18} />
              <span className="overline text-cobalt">Messages</span>
            </div>
            <div className="mt-3 font-display text-4xl text-ink">{contacts.length}</div>
          </div>
          <div className="bg-cream-alt p-6" data-testid="stat-newsletter">
            <div className="flex items-center gap-3 text-orange-brand">
              <Users size={18} />
              <span className="overline">Subscribers</span>
            </div>
            <div className="mt-3 font-display text-4xl text-ink">{newsletter.length}</div>
          </div>
        </div>

        <div className="mt-12 border-b border-tan flex gap-2">
          <Tab active={tab === "contacts"} onClick={() => setTab("contacts")} testId="tab-contacts">
            Contact submissions
          </Tab>
          <Tab active={tab === "newsletter"} onClick={() => setTab("newsletter")} testId="tab-newsletter">
            Newsletter signups
          </Tab>
        </div>

        <div className="mt-8">
          {loading ? (
            <div className="text-ink-soft py-16 text-center" data-testid="admin-loading">Loading…</div>
          ) : tab === "contacts" ? (
            contacts.length === 0 ? (
              <div className="text-ink-soft py-16 text-center border border-dashed border-tan" data-testid="empty-contacts">
                No submissions yet.
              </div>
            ) : (
              <ul className="space-y-4" data-testid="contacts-list">
                {contacts.map((c) => (
                  <li key={c.id} className="bg-cream-alt p-6 border border-tan" data-testid={`contact-item-${c.id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <div className="font-display text-xl text-ink">{c.name}</div>
                        <div className="text-sm text-ink-soft">
                          {c.organization && <span>{c.organization} · </span>}
                          <a href={`mailto:${c.email}`} className="text-cobalt hover:underline">{c.email}</a>
                        </div>
                      </div>
                      <div className="text-xs text-ink-soft">{new Date(c.created_at).toLocaleString()}</div>
                    </div>
                    <p className="mt-4 text-ink leading-relaxed whitespace-pre-wrap">{c.message}</p>
                  </li>
                ))}
              </ul>
            )
          ) : newsletter.length === 0 ? (
            <div className="text-ink-soft py-16 text-center border border-dashed border-tan" data-testid="empty-newsletter">
              No subscribers yet.
            </div>
          ) : (
            <ul className="divide-y divide-tan border border-tan" data-testid="newsletter-list">
              {newsletter.map((n) => (
                <li key={n.id} className="flex justify-between items-center px-6 py-4" data-testid={`newsletter-item-${n.id}`}>
                  <a href={`mailto:${n.email}`} className="text-cobalt hover:underline">{n.email}</a>
                  <span className="text-xs text-ink-soft">{new Date(n.created_at).toLocaleString()}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
